/**
 * Built-in door / window symbols (the client's openingGlyphs.ts) as data
 * specs — exact twins, checked against the code symbols in the client. The
 * starting points of new organisation doors / windows and "duplicate to
 * edit" of the built-in ones.
 *
 * Frame (see ObjectOpening): hinge at −u, opening toward +n, n = 0 on the
 * symbol's line. The leaves read the live `width` and `wall` themselves, so
 * their groups are `fixed` (identity).
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type ObjectShape } from '../spec';

const DOOR_WIDTH = 0.9;
const WINDOW_WIDTH = 1.2;
/** The built-in leaf panel is 5 % of the wall thick (0.01 m on the default 0.2 m wall). */
const LEAF = 'wall * 0.05';
/** Half the gap between the two glass lines. */
const GLASS = 0.025;
/** Roller-shutter box depth, beside the glass. */
const ROLLER = 0.06;

const door = (shapes: ObjectShape[]): ObjectDefinitionSpec => ({
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'WALL' },
  defaultSize: { width: DOOR_WIDTH, depth: DOOR_WIDTH, height: 2.1 },
  params: [],
  shapes,
  groups: shapes.length > 0 ? [{ id: 'leaves', name: 'Leaves', shapeIds: shapes.map((s) => s.id), resize: { u: 'fixed', n: 'fixed' } }] : [],
  model3d: null,
  opening: { kind: 'DOOR' },
});

const W = DOOR_WIDTH;
const HW = W / 2;

/** One leaf hinged at the −u jamb, swinging a quarter turn to the +u jamb. */
export const singleDoorSpec: ObjectDefinitionSpec = door([
  { id: 'leaf', name: 'Leaf', kind: 'rect', min: [-HW, 0], max: [-HW + 0.01, W], expr: { 'min.u': '-width / 2', 'max.u': `-width / 2 + ${LEAF}`, 'max.n': 'width' } },
  { id: 'swing', name: 'Swing', kind: 'arc', center: [-HW, 0], radius: W, startAngle: Math.PI / 2, endAngle: 0, expr: { 'center.u': '-width / 2', radius: 'width' } },
]);

/** Two half-width leaves hinged at both jambs, meeting in the middle. */
export const doubleDoorSpec: ObjectDefinitionSpec = door([
  { id: 'leafStart', name: 'Leaf (start)', kind: 'rect', min: [-HW, 0], max: [-HW + 0.01, HW], expr: { 'min.u': '-width / 2', 'max.u': `-width / 2 + ${LEAF}`, 'max.n': 'width / 2' } },
  { id: 'swingStart', name: 'Swing (start)', kind: 'arc', center: [-HW, 0], radius: HW, startAngle: Math.PI / 2, endAngle: 0, expr: { 'center.u': '-width / 2', radius: 'width / 2' } },
  { id: 'leafEnd', name: 'Leaf (end)', kind: 'rect', min: [HW - 0.01, 0], max: [HW, HW], expr: { 'min.u': `width / 2 - ${LEAF}`, 'max.u': 'width / 2', 'max.n': 'width / 2' } },
  { id: 'swingEnd', name: 'Swing (end)', kind: 'arc', center: [HW, 0], radius: HW, startAngle: Math.PI / 2, endAngle: Math.PI, expr: { 'center.u': 'width / 2', radius: 'width / 2' } },
]);

/** Two folding halves peaking 0.3 × the width off the wall — scales as a whole with the width. */
export const bifoldDoorSpec: ObjectDefinitionSpec = {
  ...door([
    {
      id: 'folds',
      name: 'Folds',
      kind: 'polyline',
      points: [
        [-HW, 0],
        [-HW / 2, HW * 0.6],
        [0, 0],
        [HW / 2, HW * 0.6],
        [HW, 0],
      ],
      closed: false,
    },
  ]),
  groups: [],
};

/** Just the gap: nothing drawn. */
export const openDoorwaySpec: ObjectDefinitionSpec = door([]);

const WW = WINDOW_WIDTH;
const HWW = WW / 2;

/** Double glass line with jambs; optional roller-shutter box on the outside (+n) with 5 hatch ticks. */
export const standardWindowSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'WALL' },
  defaultSize: { width: WW, depth: WW, height: 0.6 },
  params: [{ key: 'rollerShutter', kind: 'boolean', label: 'roller shutter', default: false }],
  shapes: [
    { id: 'glass', name: 'Glass', kind: 'rect', min: [-HWW, -GLASS], max: [HWW, GLASS] },
    { id: 'box', name: 'Shutter box', kind: 'rect', min: [-HWW, GLASS], max: [HWW, GLASS + ROLLER] },
    {
      id: 'tick',
      name: 'Hatch',
      kind: 'polyline',
      points: [
        [-HWW + WW / 6, GLASS],
        [-HWW + WW / 6, GLASS + ROLLER],
      ],
      closed: false,
      expr: { 'p0.u': '-width / 2 + width * (i + 1) / 6', 'p1.u': '-width / 2 + width * (i + 1) / 6' },
    },
  ],
  groups: [
    // Stretches along the wall; the glass keeps its thickness.
    { id: 'glassGroup', name: 'Glass', shapeIds: ['glass'], resize: { u: 'stretch', n: 'fixed' } },
    { id: 'shutter', name: 'Shutter box', shapeIds: ['box'], resize: { u: 'stretch', n: 'fixed' }, visibleWhen: { param: 'rollerShutter', equals: true } },
    { id: 'hatch', name: 'Hatch', shapeIds: ['tick'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { value: 5 } }, visibleWhen: { param: 'rollerShutter', equals: true } },
  ],
  model3d: null,
  opening: { kind: 'WINDOW' },
};

/** Built-in door type (DoorType value) → its data twin. */
export const DATA_BUILTIN_DOOR_SPECS: Readonly<Record<string, ObjectDefinitionSpec>> = {
  SINGLE: singleDoorSpec,
  DOUBLE: doubleDoorSpec,
  BIFOLD: bifoldDoorSpec,
  OPEN_DOORWAY: openDoorwaySpec,
};

/** Built-in window type → its data twin. */
export const DATA_BUILTIN_WINDOW_SPECS: Readonly<Record<string, ObjectDefinitionSpec>> = {
  STANDARD: standardWindowSpec,
};
